import { useEffect } from "react";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || "https://leydedatoschile.com";
const PUBLIC_SITE_URL = "https://leydedatoschile.com";
const SITE_NAME = "Ley de Datos Chile";
const DEFAULT_OG_IMAGE = `${PUBLIC_SITE_URL}/og/og-home.jpg`;

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function setHreflang(lang, url) {
  const sel = `link[rel="alternate"][hreflang="${lang}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", lang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function setJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (el) el.remove();
}

export default function SEO({
  title,
  description,
  path = "/",
  ogImage,
  keywords,
  schemas = [],
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Cumple la Ley 21.719 de Protección de Datos`;
    const url = `${PUBLIC_SITE_URL}${path}`;
    const finalImage = ogImage || DEFAULT_OG_IMAGE;

    document.title = fullTitle;
    document.documentElement.lang = "es-CL";

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    setMeta("googlebot", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    setMeta("author", SITE_NAME);

    // Local SEO Chile
    setMeta("geo.region", "CL");
    setMeta("geo.country", "Chile");
    setMeta("geo.placename", "Santiago, Chile");
    setMeta("geo.position", "-33.4489;-70.6693");
    setMeta("ICBM", "-33.4489, -70.6693");
    setMeta("language", "Spanish");
    setMeta("DC.language", "es-CL");
    setMeta("DC.coverage", "Chile");

    // Open Graph
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", url, "property");
    setMeta("og:locale", "es_CL", "property");
    setMeta("og:site_name", SITE_NAME, "property");
    setMeta("og:image", finalImage, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", finalImage);

    setCanonical(url);

    // Hreflang — sitio único en español de Chile
    if (!noindex) {
      setHreflang("es-CL", url);
      setHreflang("es", url);
      setHreflang("x-default", url);
    }

    // JSON-LD schemas
    schemas.forEach((s, i) => setJsonLd(`page-${i}`, s));

    return () => {
      schemas.forEach((_, i) => removeJsonLd(`page-${i}`));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, ogImage, keywords, noindex, JSON.stringify(schemas)]);

  return null;
}

// Helpers for schemas
export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  alternateName: ["LeyDeDatosChile", "Ley de Datos CL"],
  url: PUBLIC_SITE_URL,
  logo: `${PUBLIC_SITE_URL}/favicon.svg`,
  image: `${PUBLIC_SITE_URL}/og/og-home.jpg`,
  description:
    "Plataforma chilena que ayuda a PYMEs a cumplir con la Ley N° 21.719 de Protección de Datos Personales mediante escáner gratuito y generador de documentos legales con IA.",
  inLanguage: "es-CL",
  address: {
    "@type": "PostalAddress",
    addressCountry: "CL",
    addressRegion: "Región Metropolitana",
    addressLocality: "Santiago",
  },
  areaServed: { "@type": "Country", name: "Chile", alternateName: "CL" },
  contactPoint: {
    "@type": "ContactPoint",
    email: "privacidad@leydedatoschile.com",
    contactType: "customer support",
    availableLanguage: ["Spanish", "es-CL"],
    areaServed: "CL",
  },
  knowsAbout: [
    "Ley 21.719",
    "Protección de Datos Personales Chile",
    "Cumplimiento Normativo",
    "Agencia de Protección de Datos Personales (APDP)",
    "Política de Privacidad",
    "Derechos ARCO",
    "GDPR",
    "Ley 19.628",
    "Cláusula de Consentimiento",
    "DPA",
    "PYME Chile",
  ],
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${PUBLIC_SITE_URL}${it.path}`,
  })),
});
